// Maps API/network errors to a customer-safe display message.
// The backend already returns customer-safe strings in `error` (see the
// global error handler and route catch blocks in backend/) — this just
// extracts that safely, and falls back to a generic message for anything
// that looks like a technical leak (Prisma text, stack traces, network
// errors) so raw internals never reach the UI.

const TECHNICAL_PATTERNS = [
  'prisma', 'transaction', 'stack', 'trace', 'undefined', 'null',
  'exception', 'syntax', 'cannot read', 'is not a function', 'sql',
  'database', 'query', 'postgresql', 'econnrefused', 'failed to fetch',
];

function looksTechnical(message) {
  if (typeof message !== 'string' || !message) return true;
  const lower = message.toLowerCase();
  return TECHNICAL_PATTERNS.some((p) => lower.includes(p));
}

export function getDisplayError(err, fallback = 'Something went wrong. Please try again.') {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return 'You are offline. Please check your connection.';
  }

  // axios error shape: err.response.data.error / .message
  const backendMessage = err?.response?.data?.error || err?.response?.data?.message;
  if (typeof backendMessage === 'string' && backendMessage && !looksTechnical(backendMessage)) {
    return backendMessage;
  }

  // already-unwrapped error object (e.g. from safeFetch): { error: '...' }
  if (typeof err?.error === 'string' && err.error && !looksTechnical(err.error)) {
    return err.error;
  }

  if (err?.name === 'TypeError' && err?.message === 'Failed to fetch') {
    return 'Cannot connect to server. Please try again.';
  }

  if (!err?.response && (err?.message === 'Network Error' || err?.code === 'ERR_NETWORK')) {
    return 'Cannot connect to server. Please try again.';
  }

  return fallback;
}

// fetch()-based call sites: parses the response and throws a safe,
// consistently-shaped error ({ status, error }) instead of a raw exception.
export async function safeFetch(url, options) {
  let res;
  try {
    res = await fetch(url, options);
  } catch (err) {
    throw { error: getDisplayError(err, 'Cannot connect. Please check your connection.') };
  }

  let data = {};
  try {
    data = await res.json();
  } catch {
    // non-JSON response body — fall through with an empty data object
  }

  if (!res.ok) {
    throw { status: res.status, error: getDisplayError({ error: data.error }, data.error || 'Request failed') };
  }

  return data;
}
